import { useEffect, useState } from 'react';

import { ownerApi } from '../api/owner';
import { QuotaBanner } from '../components/QuotaBanner';
import { SuspendedBanner } from '../components/SuspendedBanner';
import { useAuthStore } from '../stores/authStore';
import type { OwnerAnalytics, OwnerBusiness, QuotaStatus } from '../types';

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-brand border border-slate-200 bg-white p-4">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value.toLocaleString('en-IN')}</p>
    </div>
  );
}

export function Dashboard() {
  const { user } = useAuthStore();
  const [business, setBusiness] = useState<OwnerBusiness | null>(null);
  const [quota, setQuota] = useState<QuotaStatus | null>(null);
  const [analytics, setAnalytics] = useState<OwnerAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [b, q, a] = await Promise.all([
          ownerApi.getBusiness(),
          ownerApi.getQuota(),
          ownerApi.getAnalytics(30),
        ]);
        setBusiness(b);
        setQuota(q);
        setAnalytics(a);
      } catch {
        setErrorMsg('Could not load your business data.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (errorMsg) return <p className="text-sm text-red-600">{errorMsg}</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Namaste, {user?.full_name} 👋</h1>
        <p className="text-sm text-slate-500">
          {business?.name} — {business?.city}
        </p>
      </div>

      {business?.is_suspended && <SuspendedBanner />}
      {quota && <QuotaBanner quota={quota} />}

      {analytics && (
        <div>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Last {analytics.period_days} days
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <StatCard label="Page views" value={analytics.page_views} />
            <StatCard label="Unique visitors" value={analytics.unique_visitors} />
            <StatCard label="QR scans" value={analytics.qr_scans} />
            <StatCard label="Calls" value={analytics.calls_clicked} />
            <StatCard label="WhatsApp clicks" value={analytics.whatsapp_clicks} />
            <StatCard label="Leads" value={analytics.leads_captured} />
            <StatCard label="Feedback" value={analytics.feedback_count} />
          </div>
        </div>
      )}
    </div>
  );
}
